/**
 * VRG-720 -- carry a visitor BACK to where they started across an OAuth round trip.
 *
 * ## The problem
 *
 * `AuthCallback` always ended with `navigate('/certificates')`. That is right for someone
 * who opened the sign-in page on purpose and wrong for a visitor who tapped "Continue with
 * Google" on a certificate's access form: they would land on their (empty) certificate list
 * instead of the certificate they scanned.
 *
 * ## Two keys, because there are two consumers
 *
 *  - `RETURN_KEY` -- WHERE to go. Consumed (read + removed) by `AuthCallback`, which is the
 *    only thing that navigates.
 *  - `ACCESS_INTENT_KEY` -- WHAT the visitor was doing: submitting the access request for a
 *    given certificate. Consumed by `RequestAccessInfo` once the signed-in profile is
 *    available, which POSTs the request with the profile's name and email.
 *
 * Splitting them keeps `AuthCallback` ignorant of certificates, and lets slice 2 (claim on
 * scan) add its own intent without touching the redirect.
 *
 * `sessionStorage`, not `localStorage`: the redirect to Google and back happens in one tab,
 * and a value that survives the tab would turn into a stale redirect on some later sign-in.
 * Both records also carry a timestamp and expire after 15 minutes for the same reason.
 *
 * Everything here is best effort: storage can be unavailable (private mode), and failing to
 * remember simply means the visitor lands on `/certificates` as before.
 */

const RETURN_KEY = 'vera:post-auth-return';
const ACCESS_INTENT_KEY = 'vera:post-auth-access-intent';
const CLAIM_INTENT_KEY = 'vera:post-auth-claim-intent';

/** 15 minutes. Long enough for a slow Google consent screen, short enough not to go stale. */
export const POST_AUTH_TTL_MS = 15 * 60 * 1000;

export interface AccessIntent {
  certificateId: string;
  /**
   * The consent + position acquired on the TAP that started the sign-in, when the gate
   * requires a location. The redirect that follows cannot raise a permission prompt (there
   * is no gesture on the way back), so the position is taken before leaving and carried.
   */
  locationConsent: boolean;
  location?: { latitude: number; longitude: number };
}

interface Stamped<T> {
  at: number;
  value: T;
}

const write = <T>(key: string, value: T) => {
  try {
    window.sessionStorage.setItem(
      key,
      JSON.stringify({ at: Date.now(), value } satisfies Stamped<T>),
    );
  } catch {
    /* storage unavailable: the visitor just lands on /certificates as before */
  }
};

const read = <T>(key: string, consume: boolean): T | null => {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Stamped<T>>;
    if (consume) window.sessionStorage.removeItem(key);
    if (typeof parsed?.at !== 'number' || Date.now() - parsed.at > POST_AUTH_TTL_MS) {
      window.sessionStorage.removeItem(key);
      return null;
    }
    return (parsed.value ?? null) as T | null;
  } catch {
    return null;
  }
};

/**
 * Only same-origin, absolute paths. `//evil.example` and `/\evil.example` are protocol-
 * relative redirects in disguise, so anything that is not a plain `/path` is refused.
 */
export const isSafeReturnPath = (path: unknown): path is string =>
  typeof path === 'string' &&
  path.startsWith('/') &&
  !path.startsWith('//') &&
  !path.includes('\\') &&
  !/[\r\n]/.test(path);

export const rememberReturnPath = (path: string) => {
  if (isSafeReturnPath(path)) write(RETURN_KEY, path);
};

/** Read AND remove. `null` when none was stored, it expired, or it is not a safe path. */
export const consumeReturnPath = (): string | null => {
  const path = read<string>(RETURN_KEY, true);
  return isSafeReturnPath(path) ? path : null;
};

export const rememberAccessIntent = (intent: AccessIntent) => write(ACCESS_INTENT_KEY, intent);

/** Peek without consuming, so a page can show a "finishing sign in" state while it waits. */
export const peekAccessIntent = (certificateId: string): AccessIntent | null => {
  const intent = read<AccessIntent>(ACCESS_INTENT_KEY, false);
  return intent?.certificateId === certificateId ? intent : null;
};

/**
 * Read AND remove -- the idempotency guard. Whatever consumes the intent owns submitting it,
 * and a second mount/effect pass finds nothing, so the request cannot be POSTed twice.
 */
export const consumeAccessIntent = (certificateId: string): AccessIntent | null => {
  const intent = peekAccessIntent(certificateId);
  if (intent) clearAccessIntent();
  return intent;
};

export const clearAccessIntent = () => {
  try {
    window.sessionStorage.removeItem(ACCESS_INTENT_KEY);
  } catch {
    /* nothing to do */
  }
};

/**
 * VRG-720 slice 2 -- the visitor was claiming an item (claim on scan) when they left for
 * Google. Carries the CLAIM TICKET, because the page they come back to has no `vt` in its
 * URL (the return path is stored without the query string) and the ticket is the only proof
 * of the physical tap. It lives in sessionStorage next to the other intents, expires with
 * them, and is consumed exactly once.
 */
export interface ClaimIntent {
  certificateId: string;
  ticket: string;
  /** Mirrors `AccessIntent`: the position taken on the tap, because the return has no gesture. */
  locationConsent: boolean;
  location?: { latitude: number; longitude: number };
}

export const rememberClaimIntent = (intent: ClaimIntent) => write(CLAIM_INTENT_KEY, intent);

export const peekClaimIntent = (certificateId: string): ClaimIntent | null => {
  const intent = read<ClaimIntent>(CLAIM_INTENT_KEY, false);
  return intent?.certificateId === certificateId ? intent : null;
};

/** Read AND remove -- the idempotency guard, same contract as `consumeAccessIntent`. */
export const consumeClaimIntent = (certificateId: string): ClaimIntent | null => {
  const intent = peekClaimIntent(certificateId);
  if (intent) clearClaimIntent();
  return intent;
};

export const clearClaimIntent = () => {
  try {
    window.sessionStorage.removeItem(CLAIM_INTENT_KEY);
  } catch {
    /* nothing to do */
  }
};
